import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import { getLatestNews, getPopularMovies, getActorById, shortDate, TYPE_LABELS } from "@/lib/media";

export default function NewsAndRanking() {
  const latestNews = getLatestNews(4);
  const popular = getPopularMovies(5);
  const spotlightActor = getActorById(1);

  return (
    <section className="shell mt-12 md:mt-16" aria-label="影视动态与热门榜单">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-8">
          <SectionHeading
            eyebrow="Newsroom"
            title="影视动态"
            desc="定档、幕后与行业观察，每天更新"
            moreTo="/news"
            moreLabel="全部动态"
          />

          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <Link
              to={`/news/${latestNews[0].id}`}
              className="group grid cursor-pointer grid-cols-1 sm:grid-cols-2"
            >
              <div className="relative h-44 w-full overflow-hidden bg-secondary-100 sm:h-full">
                <img
                  src={latestNews[0].cover}
                  alt={latestNews[0].title}
                  title={`${latestNews[0].title} 影视动态配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-col justify-center p-4 md:p-5">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-foreground-500">
                  <span className="rounded-md bg-accent-100 px-2 py-0.5 text-accent-800">
                    {latestNews[0].category}
                  </span>
                  <span>{latestNews[0].date}</span>
                  <span>{latestNews[0].readTime}</span>
                </div>
                <h3 className="mt-2.5 line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                  {latestNews[0].title}
                </h3>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                  {latestNews[0].excerpt}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-700">
                  阅读全文
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>

            <div className="divide-y divide-background-200 border-t border-background-200">
              {latestNews.slice(1).map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex cursor-pointer items-start gap-3 px-4 py-3.5 transition-colors hover:bg-background-100 md:px-5"
                >
                  <span className="mt-0.5 flex h-6 shrink-0 items-center rounded-md bg-secondary-100 px-2 text-[11px] font-medium text-secondary-800">
                    {item.category}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="line-clamp-1 text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h4>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                      {shortDate(item.date)} · {item.excerpt}
                    </p>
                  </div>
                  <i className="ri-arrow-right-up-line mt-1 shrink-0 text-sm text-foreground-300 transition-colors group-hover:text-primary-500" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <SectionHeading eyebrow="Ranking" title="热度榜单" desc="本周站内阅读与讨论热度" />
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
            <ol className="space-y-1">
              {popular.map((movie, index) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex cursor-pointer items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-50"
                  >
                    <span
                      className={`font-heading flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold ${
                        index < 3 ? "bg-primary-500 text-background-50" : "bg-background-300 text-foreground-700"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-secondary-100">
                      <img
                        src={movie.poster}
                        alt={`《${movie.title}》封面`}
                        title={movie.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                        {movie.title}
                      </p>
                      <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
                        {TYPE_LABELS[movie.type]} · {movie.year} · {movie.region}
                      </p>
                    </div>
                    <span className="flex shrink-0 items-center gap-0.5 font-label text-xs font-bold text-accent-700">
                      <i className="ri-fire-line" />
                      {movie.popularity}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>

            {spotlightActor && (
              <div className="mt-4 rounded-md border border-background-300/70 bg-background-50 p-3.5">
                <p className="font-label text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground-400">
                  本周人物
                </p>
                <div className="mt-2.5 flex items-center gap-3">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-secondary-100">
                    <img
                      src={spotlightActor.avatar}
                      alt={`${spotlightActor.name} 头像`}
                      title={spotlightActor.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/actor/${spotlightActor.id}`}
                      className="block text-sm font-semibold text-foreground-950 hover:text-primary-700"
                    >
                      {spotlightActor.name}
                    </Link>
                    <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
                      {spotlightActor.tags.slice(0, 2).join(" · ")}
                    </p>
                  </div>
                  <Link
                    to={`/actor/${spotlightActor.id}`}
                    aria-label={`查看 ${spotlightActor.name} 的资料`}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-4">
            <div className="flex items-start gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-megaphone-line" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">剧集更新时间提醒</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  《星轨之上》每周四更新，《云端旅店》每周日更新，收藏后可在详情页随时查看进度。
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}